'use client';

import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { cn } from '@/utils/cn-tw-merge';

const TOAST_TEST_IDS = {
  default: DataTestIdEnum.TOAST_DEFAULT,
  info: DataTestIdEnum.TOAST_INFO,
  warn: DataTestIdEnum.TOAST_WARN,
  error: DataTestIdEnum.TOAST_ERROR,
  success: DataTestIdEnum.TOAST_SUCCESS,
} as const;

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:top-12 sm:left-12 sm:bottom-auto sm:flex-col sm:w-fit sm:max-w-screen-sm',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-2xl border border-slate-200 p-3 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=closed]:sm:slide-out-to-left-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-top-full',
  {
    variants: {
      variant: {
        default: 'border bg-white text-slate-950',
        info: 'info group border-blue-300 bg-blue-100 text-blue-600',
        warn: 'warn group border-warning-300 bg-warning-100 text-warning-700',
        error: 'error group border-danger-300 bg-danger-100 text-danger-600',
        success: 'success group border-success-300 bg-success-100 text-success-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type ToastJsxIcon = IconDefinition;

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>
  & VariantProps<typeof toastVariants> & {
    icon?: ToastJsxIcon;
  }
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
      data-testid={TOAST_TEST_IDS[variant ?? 'default']}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'flex items-center justify-center bg-transparent text-base font-bold underline underline-offset-2 disabled:pointer-events-none disabled:opacity-50 ',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'w-5 h-5 text-xl p-[0.125rem] rounded-full text-slate-950/50 hover:text-slate-950 focus:outline-none focus:ring-2 group-[.info]:text-blue-600 group-[.info]:hover:bg-blue-200 group-[.warn]:text-warning-700 group-[.warn]:hover:bg-warning-200 group-[.error]:text-danger-700 group-[.error]:hover:bg-danger-200 group-[.success]:text-success-700 group-[.success]:hover:bg-success-200',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-left font-semibold flex flex-wrap text-wrap', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastIcon = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      'text-sm w-6 h-6 min-w-6 min-h-6 text-white group-[.info]:bg-blue-600 group-[.warn]:bg-warning-600 group-[.error]:bg-danger-600 group-[.success]:bg-success-600',
      className,
    )}
    {...props}
  />
));
ToastIcon.displayName = 'ToastIcon';

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastClose,
  ToastAction,
  ToastIcon,
};
